import { apiClientWeb } from "../../../lib/apiClientWeb";

export interface MundoEducativo {
  idMundoEducativo: number;
  nombre: string;
  descripcion?: string;
  icono?: string;
  orden: number;
  vigente: boolean;
  fechaCreacion: string;
  fechaActualizacion: string;
  modulos?: ModuloEducativo[];
}

export interface ModuloEducativo {
  idModuloEducativo: number;
  mundoId: number;
  nombre: string;
  descripcion?: string;
  orden: number;
  vigente: boolean;
  fechaCreacion: string;
  fechaActualizacion: string;
  contenidos?: ContenidoEducativo[];
}

export interface ContenidoEducativo {
  idContenidoEducativo: number;
  moduloId: number;
  tipoContenido: "CONCEPTOS" | "FORMULAS" | "EJEMPLOS" | "EJERCICIOS";
  titulo?: string;
  contenido: string;
  imagenUrl?: string;
  orden: number;
  vigente: boolean;
  fechaCreacion: string;
  fechaActualizacion: string;
  preguntas?: PreguntaEjercicioEducativo[];
}

export interface PreguntaEjercicioEducativo {
  idPreguntaEjercicioEducativo: number;
  contenidoId: number;
  tipoPregunta: "ASOCIACION" | "COMPLETAR" | "SELECCION_MULTIPLE" | "ORDENAR" | "UBICAR_RECTA";
  pregunta: string;
  opciones: Array<{
    valor: string;
    texto: string;
    esCorrecta: boolean;
  }>;
  respuestaCorrecta: string;
  explicacion?: string;
  imagenUrl?: string;
  orden: number;
  vigente: boolean;
  fechaCreacion: string;
  fechaActualizacion: string;
}

export interface CreateMundoDto {
  nombre: string;
  descripcion?: string;
  icono?: string;
  orden?: number;
}

export interface UpdateMundoDto {
  nombre?: string;
  descripcion?: string;
  icono?: string;
  orden?: number;
  vigente?: boolean;
}

export interface CreateModuloDto {
  mundoId: number;
  nombre: string;
  descripcion?: string;
  orden?: number;
}

export interface UpdateModuloDto {
  nombre?: string;
  descripcion?: string;
  orden?: number;
  vigente?: boolean;
}

export interface CreateContenidoDto {
  moduloId: number;
  tipoContenido: "CONCEPTOS" | "FORMULAS" | "EJEMPLOS" | "EJERCICIOS";
  titulo?: string;
  contenido: string;
  imagenUrl?: string;
  orden?: number;
}

export interface UpdateContenidoDto {
  tipoContenido?: "CONCEPTOS" | "FORMULAS" | "EJEMPLOS" | "EJERCICIOS";
  titulo?: string;
  contenido?: string;
  imagenUrl?: string;
  orden?: number;
  vigente?: boolean;
}

export interface CreatePreguntaDto {
  contenidoId: number;
  tipoPregunta: "ASOCIACION" | "COMPLETAR" | "SELECCION_MULTIPLE" | "ORDENAR" | "UBICAR_RECTA";
  pregunta: string;
  opciones: Array<{
    valor: string;
    texto: string;
    esCorrecta: boolean;
  }>;
  respuestaCorrecta: string;
  explicacion?: string;
  imagenUrl?: string;
  orden?: number;
}

export interface UpdatePreguntaDto {
  tipoPregunta?: "ASOCIACION" | "COMPLETAR" | "SELECCION_MULTIPLE" | "ORDENAR" | "UBICAR_RECTA";
  pregunta?: string;
  opciones?: Array<{
    valor: string;
    texto: string;
    esCorrecta: boolean;
  }>;
  respuestaCorrecta?: string;
  explicacion?: string;
  imagenUrl?: string;
  orden?: number;
  vigente?: boolean;
}

class EjerciciosEducativosService {
  // ========== MUNDOS ==========
  async getMundos(): Promise<MundoEducativo[]> {
    const response = await apiClientWeb.get("/api/ejercicios-educativos/mundos");
    return response.data.data || [];
  }

  async getMundoById(id: number): Promise<MundoEducativo> {
    const response = await apiClientWeb.get(`/api/ejercicios-educativos/mundos/${id}`);
    return response.data.data;
  }

  async createMundo(data: CreateMundoDto): Promise<MundoEducativo> {
    const response = await apiClientWeb.post("/api/ejercicios-educativos/admin/mundos", data);
    return response.data.data;
  }

  async updateMundo(id: number, data: UpdateMundoDto): Promise<MundoEducativo> {
    const response = await apiClientWeb.patch(`/api/ejercicios-educativos/admin/mundos/${id}`, data);
    return response.data.data;
  }

  async deleteMundo(id: number): Promise<void> {
    await apiClientWeb.delete(`/api/ejercicios-educativos/admin/mundos/${id}`);
  }

  // ========== MÓDULOS ==========
  async getModuloById(id: number): Promise<ModuloEducativo> {
    const response = await apiClientWeb.get(`/api/ejercicios-educativos/modulos/${id}`);
    return response.data.data;
  }

  async createModulo(data: CreateModuloDto): Promise<ModuloEducativo> {
    const response = await apiClientWeb.post("/api/ejercicios-educativos/admin/modulos", data);
    return response.data.data;
  }

  async updateModulo(id: number, data: UpdateModuloDto): Promise<ModuloEducativo> {
    const response = await apiClientWeb.patch(`/api/ejercicios-educativos/admin/modulos/${id}`, data);
    return response.data.data;
  }

  async deleteModulo(id: number): Promise<void> {
    await apiClientWeb.delete(`/api/ejercicios-educativos/admin/modulos/${id}`);
  }

  // ========== CONTENIDOS ==========
  async getContenidoById(id: number): Promise<ContenidoEducativo> {
    const response = await apiClientWeb.get(`/api/ejercicios-educativos/contenidos/${id}`);
    return response.data.data;
  }

  async createContenido(data: CreateContenidoDto): Promise<ContenidoEducativo> {
    const response = await apiClientWeb.post("/api/ejercicios-educativos/admin/contenidos", data);
    return response.data.data;
  }

  async updateContenido(id: number, data: UpdateContenidoDto): Promise<ContenidoEducativo> {
    const response = await apiClientWeb.patch(`/api/ejercicios-educativos/admin/contenidos/${id}`, data);
    return response.data.data;
  }

  async deleteContenido(id: number): Promise<void> {
    await apiClientWeb.delete(`/api/ejercicios-educativos/admin/contenidos/${id}`);
  }

  // ========== PREGUNTAS ==========
  async getPreguntaById(id: number): Promise<PreguntaEjercicioEducativo> {
    const response = await apiClientWeb.get(`/api/ejercicios-educativos/preguntas/${id}`);
    return response.data.data;
  }

  async createPregunta(data: CreatePreguntaDto): Promise<PreguntaEjercicioEducativo> {
    const response = await apiClientWeb.post("/api/ejercicios-educativos/admin/preguntas", data);
    return response.data.data;
  }

  async updatePregunta(id: number, data: UpdatePreguntaDto): Promise<PreguntaEjercicioEducativo> {
    const response = await apiClientWeb.patch(`/api/ejercicios-educativos/admin/preguntas/${id}`, data);
    return response.data.data;
  }

  async deletePregunta(id: number): Promise<void> {
    await apiClientWeb.delete(`/api/ejercicios-educativos/admin/preguntas/${id}`);
  }
}

export const ejerciciosEducativosService = new EjerciciosEducativosService();
